import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';
import { SortableHeader } from '@/components/ui/sortable-header';
import { useTableData } from '@/hooks/useTableData';
import {
  Edit2,
  Trash2,
  MapPin,
  Power,
  PowerOff
} from 'lucide-react';
import type { Location } from '../../types';

interface LocationsTableProps {
  locations: Location[];
  onEdit: (location: Location) => void;
  onDelete: (location: Location) => void;
  onToggleStatus: (location: Location, newStatus: Location['estado']) => void;
  loading?: boolean;
}

type SortField = 'nombre' | 'direccion' | 'tipoLocal' | 'estado' | 'fechaApertura';

export const LocationsTable: React.FC<LocationsTableProps> = ({
  locations,
  onEdit,
  onDelete,
  onToggleStatus,
  loading = false,
}) => {
  // Configuración del hook reutilizable
  const fieldLabels: Record<SortField, string> = {
    nombre: 'Sucursal',
    direccion: 'Ubicación',
    tipoLocal: 'Tipo',
    estado: 'Estado',
    fechaApertura: 'Fecha Apertura'
  };

  // Extractor personalizado para campos complejos
  const sortValueExtractor = (location: Location, field: SortField): any => {
    switch (field) {
      case 'nombre':
        return location.nombre.toLowerCase();
      case 'direccion':
        return location.direccion.toLowerCase();
      case 'tipoLocal':
        return location.tipoLocal;
      case 'estado':
        return location.estado;
      case 'fechaApertura':
        return new Date(location.fechaApertura);
      default:
        return location.nombre;
    }
  };

  // Usar el hook reutilizable
  const tableData = useTableData<Location, SortField>(
    {
      data: locations,
      defaultSortField: 'nombre',
      defaultSortDirection: 'asc',
      itemsPerPage: 10,
      sortValueExtractor
    },
    fieldLabels
  );
  const getStatusColor = (estado: Location['estado']) => {
    switch (estado) {
      case 'ACTIVO':
        return 'bg-green-100 text-green-700';
      case 'INACTIVO':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getTypeColor = (tipo: Location['tipoLocal']) => {
    switch (tipo) {
      case 'PRINCIPAL':
        return 'bg-blue-50 text-blue-600';
      case 'SUCURSAL':
        return 'bg-purple-50 text-purple-600';
      default:
        return 'bg-gray-50 text-gray-600';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-PE');
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-macartur-primary mx-auto mb-4"></div>
        <p className="text-gray-600">Cargando sucursales...</p>
      </div>
    );
  }

  if (locations.length === 0) {
    return (
      <div className="text-center py-12">
        <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No hay sucursales</h3>
        <p className="text-gray-500">No se encontraron sucursales con los filtros aplicados.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full">
        <thead className="bg-gray-50 border-b">
          <tr>
            <SortableHeader
              field="nombre"
              onSort={tableData.handleSort}
              getSortIcon={tableData.getSortIcon}
              className="text-left"
            >
              Sucursal
            </SortableHeader>
            <SortableHeader
              field="direccion"
              onSort={tableData.handleSort}
              getSortIcon={tableData.getSortIcon}
              className="text-left"
            >
              Ubicación
            </SortableHeader>
            <SortableHeader
              field="tipoLocal"
              onSort={tableData.handleSort}
              getSortIcon={tableData.getSortIcon}
              className="text-center"
            >
              Tipo
            </SortableHeader>
            <SortableHeader
              field="estado"
              onSort={tableData.handleSort}
              getSortIcon={tableData.getSortIcon}
              className="text-center"
            >
              Estado
            </SortableHeader>
            <SortableHeader
              field="fechaApertura"
              onSort={tableData.handleSort}
              getSortIcon={tableData.getSortIcon}
              className="text-center"
            >
              Fecha Apertura
            </SortableHeader>
            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              Acciones
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {tableData.pagination.paginatedData.map((location) => {
            return (
              <tr key={location.id} className="hover:bg-gray-50">
                <td className="px-4 py-4">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {location.nombre}
                    </div>
                    <div className="text-xs text-gray-400">
                      ID: {location.id}
                    </div>
                  </div>
                </td>
                
                <td className="px-4 py-4">
                  <div className="text-sm text-gray-900">
                    <div className="flex items-center gap-1">
                      <MapPin className="h-3 w-3 text-gray-400" />
                      {location.direccion}
                    </div>
                  </div>
                </td>
                
                <td className="px-4 py-4 text-center">
                  <Badge 
                    variant="outline" 
                    className={`${getTypeColor(location.tipoLocal)} border-0`}
                  >
                    {location.tipoLocal}
                  </Badge>
                </td>
                
                <td className="px-4 py-4 text-center">
                  <Badge className={getStatusColor(location.estado)}>
                    {location.estado}
                  </Badge>
                </td>
                
                <td className="px-4 py-4 text-center">
                  <div className="text-sm text-gray-900">
                    {formatDate(location.fechaApertura)}
                  </div>
                </td>
                
                <td className="px-4 py-4 text-center">
                  <div className="flex gap-2 justify-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEdit(location)}
                      className="text-xs"
                    >
                      <Edit2 className="w-3 h-3 mr-1" />
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onToggleStatus(location, location.estado === 'ACTIVO' ? 'INACTIVO' : 'ACTIVO')}
                      className={`text-xs ${
                        location.estado === 'ACTIVO'
                          ? 'text-orange-600 hover:text-orange-700 hover:bg-orange-50'
                          : 'text-green-600 hover:text-green-700 hover:bg-green-50'
                      }`}
                    >
                      {location.estado === 'ACTIVO' ? (
                        <PowerOff className="w-3 h-3 mr-1" />
                      ) : (
                        <Power className="w-3 h-3 mr-1" />
                      )}
                      {location.estado === 'ACTIVO' ? 'Desactivar' : 'Activar'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onDelete(location)}
                      className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
        </table>
      </div>

      {/* Sort info */}
      <div className="mt-2 text-sm text-gray-500">
        Ordenado por: <span className="font-medium">
          {tableData.sortInfo.label}
        </span> ({tableData.sortInfo.direction === 'asc' ? 'Ascendente' : 'Descendente'})
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={tableData.pagination.currentPage}
        totalPages={tableData.pagination.totalPages}
        onPageChange={tableData.pagination.goToPage}
        showingFrom={tableData.pagination.showingFrom}
        showingTo={tableData.pagination.showingTo}
        totalItems={tableData.pagination.totalItems}
        itemsPerPage={tableData.pagination.itemsPerPage}
        onItemsPerPageChange={tableData.pagination.setItemsPerPage}
      />
    </div>
  );
};